import type {RoiRedeploymentGrid} from 'src/types/roi';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import moment from 'moment';
import memoize from 'lodash/memoize';

import {prettifyNumber} from 'src/utils';

import RetentionGrid from 'src/components/lib/retention-grid';
import pure from 'src/flux/pure';

import css from './redeployment-grid.css';


const RedeploymentGrid = ({
  data = [],
  loading,
}: {
  data: RoiRedeploymentGrid;
  loading?: boolean;
}) => {
  const {t, language} = useLabelTranslation();
  const showYear = data.length > 12;
  const gridData = data.map(
    ({redeploymentRate, redeployments, numEnds, month}) => ({
      y: getFullMonth(month, showYear, language),
      month,
      numEnds,
      redeploymentRate: `${redeploymentRate}%`,
      ...Object.keys(redeployments).reduce<Record<string, any>>(
        (counts, month) => ({...counts, [month]: redeployments[month].length}),
        {},
      ),
    }),
  );
  const [start] = data;
  const redeployments = start ? start.redeployments : {};
  const redeploymentMonths = Object.keys(redeployments)
    .sort(
      (monthA, monthB) =>
        getMonthOffset(monthA, start.month) -
        getMonthOffset(monthB, start.month),
    )
    .map((month) => {
      const monthOffset = getMonthOffset(month, start.month);
      const label =
        monthOffset === 0 ? `<1 ${t('month', 'month')}` : monthOffset;
      return {
        label,
        value: month,
      };
    });
  const headers = [
    {label: t('People', 'People'), value: 'numEnds'},
    ...redeploymentMonths,
    {label: t('RD_Rate', 'RD Rate'), value: 'redeploymentRate'},
  ];
  const dataColumns = redeploymentMonths.map(({value}) => value);

  return (
    <RetentionGrid
      className={css.grid}
      data={gridData}
      // @ts-expect-error - TS2769 - No overload matches this call.
      headers={headers}
      yKey="y"
      yTitle={`${t('Ended_in', 'Ended in')}...`}
      dataColumns={dataColumns}
      title={`${t(
        'The_number_of_months_until_a_contractor_was_redeployed',
        'The number of months until a contractor was redeployed',
      )}...`}
      getTooltipText={(dataRow, month) => {
        // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
        const monthOffset = getMonthOffset(month, dataRow.month) || '<1';
        return `${dataRow[month]} ${t(
          'people_redeployed_within',
          'people redeployed within',
        )} ${monthOffset} ${t('month', 'month')}${
          monthOffset !== 1 ? 's' : ''
        } (${dataRow.redeploymentRate} ${t('RD_rate', 'RD rate')})`;
      }}
      loading={loading}
    />
  );
};

const getFullMonth = (month: string, showYear: boolean, language?: string) =>
  moment(month)
    .locale(language || 'en')
    .format(showYear ? 'MMMM YYYY' : 'MMMM');
const getMonthOffset = (month: string, startMonth: string) =>
  moment(month).diff(moment(startMonth), 'months');

// @ts-expect-error - TS2322 - Type '[RoiRedeploymentGrid] & MemoizedFunction' is not assignable to type '(arg1: RoiRedeploymentGrid) => string'.
export const getAverageRedeploymentDays: (arg1: RoiRedeploymentGrid) => string =
  // @ts-expect-error - TS2558 - Expected 1 type arguments, but got 2. | TS7006 - Parameter 'data' implicitly has an 'any' type.
  memoize<[RoiRedeploymentGrid], string>((data) => {
    const {totalCount, totalOffsets} = data.reduce(
      // @ts-expect-error - TS7006 - Parameter 'prev' implicitly has an 'any' type. | TS7031 - Binding element 'redeploymentMonth' implicitly has an 'any' type. | TS7031 - Binding element 'redeployments' implicitly has an 'any' type.
      (prev, {month: redeploymentMonth, redeployments}) => {
        let count = 0;
        let offsets = 0;
        for (const month in redeployments) {
          const offset = moment(month).diff(moment(redeploymentMonth), 'days');
          count += redeployments[month].length;
          offsets += offset * redeployments[month].length;
        }
        return {
          totalCount: prev.totalCount + count,
          totalOffsets: prev.totalOffsets + offsets,
        };
      },
      {
        totalCount: 0,
        totalOffsets: 0,
      },
    );

    // '<1' covers edge case where all redeployments happen in the same month
    if (totalCount === 0 || totalOffsets === 0) {
      return '<1';
    }
    return prettifyNumber(totalOffsets / totalCount);
  });

export default pure()(RedeploymentGrid) as typeof RedeploymentGrid;
